import * as React from "react"
import { reportActivityError } from "../activity"
import { initialiseStripeCheckout, loadStripeJs } from "../stripe"
import type { HireBookingProps } from "../types"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "clientSecret" | "setStripeReady" | "checkoutRef" | "actionsRef" | "setStripeCanConfirm" | "setError" | "customer">
    & { props: HireBookingProps }

export function useStripeCheckout(inputs: Inputs) {
    const {
        clientSecret,
        setStripeReady,
        props,
        checkoutRef,
        actionsRef,
        setStripeCanConfirm,
        setError,
        customer,
    } = inputs
    React.useEffect(() => {
        if (!props.stripePublishableKey) return
        // Start downloading on the booking step; the session-specific effect
        // below still owns initialization and customer-visible errors.
        void loadStripeJs().catch(() => {})
    }, [props.stripePublishableKey])
    React.useEffect(() => {
        if (!clientSecret) return
        let cancelled = false
        setStripeReady(false)
        loadStripeJs().then(async () => {
            if (cancelled || !window.Stripe) return
            const checkout = initialiseStripeCheckout(window.Stripe(props.stripePublishableKey), {
                clientSecret,
                defaultValues: { billingAddress: { ...(customer.name ? { name: customer.name } : {}), address: { country: "GB" } } },
                elementsOptions: {
                    appearance: { theme: "stripe", variables: { colorPrimary: props.primaryColour, borderRadius: "6px", fontSizeBase: "16px" } },
                    savedPaymentMethod: { enableSave: "never", enableRedisplay: "never" },
                    syncAddressCheckbox: "none",
                },
            })
            const loaded = await checkout.loadActions()
            if (cancelled) return
            if (loaded.type === "error") throw new Error(loaded.error.message || "Stripe could not load checkout.")
            checkoutRef.current = checkout
            actionsRef.current = loaded.actions
            checkout.on("change", (session) => {
                if (cancelled) return
                setStripeCanConfirm(Boolean(session.canConfirm))

            })
            setStripeReady(true)
        }).catch((reason) => { if (!cancelled) { reportActivityError("STRIPE_LOAD_FAILED"); setError(reason.message || "Stripe could not load checkout.") } })
        return () => { cancelled = true }
    }, [clientSecret, props.primaryColour, props.stripePublishableKey])

}
